import { Router, watchBusy } from "@frui.ts/screens";
import { computed, observable } from "mobx";
import type { interfaces } from "inversify";
import download from "downloadjs";
import EventBus from "services/eventBus";
import OptionsService from "services/optionsService";
import LocalizationService from "services/localizationService";
import AppRouter from "services/router";
import SecurityService from "services/securityService";
import BankExportRepository from "repositories/bankExportRepository";
import BankExportDetailViewModel from "../bankExportDetailViewModel";
import BankExportDetailListViewModel from "../bankExportDetailListViewModel";
import NotificationService from "services/notificationService";
import { getBankExportFilename } from "../utils";
import InternalTransfersListViewModel from "./internalTransfersListViewModel";
import parseFileNameFromAxiosResponse from "utils/parseFileNameFromAxiosResponse";
import InternalTransferExportDetailResponse from "entities/internalTransferExportDetailResponse";

@Router.registerRoute({ route: ":internalTransferId", name: Router.Self })
export default class InternalTransferDetailViewModel extends BankExportDetailViewModel<InternalTransferExportDetailResponse> {
  @observable fileResponse: any = undefined;

  constructor(
    public itemId: string,
    public bankExportDetailListViewModel: ReturnType<typeof BankExportDetailListViewModel.Factory>,
    public repository: BankExportRepository,
    public localizationService: LocalizationService,
    public appRouter: AppRouter,
    public optionsService: OptionsService,
    public eventBus: EventBus,
    public securityService: SecurityService,
    public notificationService: NotificationService
  ) {
    super(
      itemId,
      bankExportDetailListViewModel,
      localizationService,
      appRouter,
      optionsService,
      eventBus,
      securityService,
      notificationService
    );
    this.navigationName = this.itemId;
  }

  @computed
  get editLabel() {
    return this.translate("internalTransferEdit");
  }

  backLink() {
    return this.appRouter.navigate(InternalTransfersListViewModel);
  }

  protected async loadDetail(): Promise<InternalTransferExportDetailResponse> {
    this.item = await this.repository.getInternalTransferById(this.itemId);
    return this.item;
  }

  @watchBusy
  async download(item: InternalTransferExportDetailResponse) {
    try {
      if (this.allowToDownload) {
        this.fileResponse = await this.repository.getInternalTransferReport(item.id);
        download(
          this.fileResponse.data,
          parseFileNameFromAxiosResponse(this.fileResponse, getBankExportFilename(item)),
          "application/zip"
        );
      }
    } catch (exc) {}
  }

  static Factory({ container }: interfaces.Context) {
    return (itemId: string) => {
      return new InternalTransferDetailViewModel(
        itemId,
        container.get(BankExportDetailListViewModel.Factory),
        container.get(BankExportRepository),
        container.get(LocalizationService),
        container.get(AppRouter),
        container.get(OptionsService),
        container.get(EventBus),
        container.get(SecurityService),
        container.get(NotificationService)
      );
    };
  }
}
